"use client";

import {
  ThemeColorField,
  useUpdateVenuePageTheme,
} from "../../hooks/venuePages/useUpdateVenuePageTheme";

import { ColorPickerField } from "@punchup/ui-common/components/common";
import { VenuePagesRow } from "@punchup/data";
import { useState } from "react";
import { useVenuePageTheme } from "../../hooks/venuePages/useVenuePageTheme";

interface Props {
  venuePage: VenuePagesRow;
}

type ColorPickerConfig =
  | {
      field: ThemeColorField;
      title: string;
      type: "color_input";
      description: string;
    }
  | {
      type: "spacer";
    };

const colorPickerConfigs: ColorPickerConfig[] = [
  {
    type: "color_input",
    field: "accent_color",
    title: "Primary Button Color",
    description: "The accent color used for buttons and highlights",
  },
  // add a "spacer" element after primary button color
  { type: "spacer" },
  {
    type: "color_input",
    field: "top_section_background_color",
    title: "Hero Banner Background",
    description: "Background color of the top section of your page",
  },
  {
    type: "color_input",
    field: "top_section_gradient_color",
    title: "Hero Banner Gradient",
    description: "Optional gradient overlay color for the top section",
  },
  {
    type: "color_input",
    field: "header_background_color",
    title: "Top Menu Bar Background",
    description: "Background color of your page header",
  },
  {
    type: "color_input",
    field: "header_border_color",
    title: "Top Menu Bar Border",
    description: "Border color on the bottom of the header",
  },
];

export default function VenueThemeSettings({ venuePage }: Props) {
  const theme = useVenuePageTheme(venuePage.slug);
  const mutation = useUpdateVenuePageTheme(venuePage.slug);

  // Track which field is currently being updated
  const [activeField, setActiveField] = useState<ThemeColorField | null>(null);

  const handleColorChange = (field: ThemeColorField, value: string) => {
    setActiveField(field);
    mutation.mutate({
      venuePageId: venuePage.id,
      venuePageSlug: venuePage.slug,
      field,
      value,
    });
  };

  const getColorValue = (field: ThemeColorField): string => {
    switch (field) {
      case "accent_color":
        return theme.accentColor;
      case "header_background_color":
        return theme.headerBackgroundColor;
      case "header_border_color":
        return theme.headerBorderColor;
      case "top_section_background_color":
        return theme.topSectionBackgroundColor;
      case "top_section_gradient_color":
        // render as top section background color if there's no gradient color set
        return theme.topSectionGradientColor ?? theme.topSectionBackgroundColor;
      default:
        return "#000000";
    }
  };

  return (
    <div className="flex flex-col md:grid md:grid-cols-2 gap-x-10 gap-y-5">
      {colorPickerConfigs.map((config) =>
        config.type === "spacer" ? (
          <div className="hidden md:block" />
        ) : (
          <ColorPickerField
            key={config.field}
            title={config.title}
            description={config.description}
            value={getColorValue(config.field)}
            onChange={(value) => handleColorChange(config.field, value)}
            disabled={mutation.isPending && activeField === config.field}
            loading={mutation.isPending && activeField === config.field}
            loadingMessage="Updating..."
            success={mutation.isSuccess && activeField === config.field}
            successMessage="Updated!"
            error={mutation.isError && activeField === config.field}
            errorMessage={
              mutation.isError && activeField === config.field
                ? (mutation.error?.message ?? "An error occurred")
                : undefined
            }
          />
        )
      )}
    </div>
  );
}
